import { useEffect, useState } from 'react';
import type { InventoryRowType } from '../types/InventoryRowType';
import { getInventoryRowTypes } from '../api/inventoryRowTypes';

// Module-level cache shared per page lifetime (lazy-loaded on first use, e.g.
// InventoryPage mount; cleared on logout). Mirrors useSources.
let cache: InventoryRowType[] | null = null;
let inflight: Promise<InventoryRowType[]> | null = null;

function load(): Promise<InventoryRowType[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = getInventoryRowTypes()
    .then((data) => {
      cache = data;
      inflight = null;
      return data;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

export function clearInventoryRowTypesCache(): void {
  cache = null;
  inflight = null;
}

interface Result {
  data: InventoryRowType[] | null;
  loading: boolean;
  error: string | null;
}

export function useInventoryRowTypes(): Result {
  const [state, setState] = useState<Result>(() =>
    cache ? { data: cache, loading: false, error: null } : { data: null, loading: true, error: null },
  );

  useEffect(() => {
    if (cache) {
      setState((prev) => (prev.data === cache ? prev : { data: cache, loading: false, error: null }));
      return;
    }
    let cancelled = false;
    load()
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, loading: false, error: message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
